import BackButton from '@/components/ui/BackButton'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'
import StatStripSkeleton from '@/components/ui/StatStripSkeleton'
import { BASKET_GRID, BASKET_SEARCH } from '@/screens/baskets/components/basket-layout'

/**
 * The baskets pages' pending blocks: a light module of bars only, since a route's pending
 * component ships in the entry chunk (docs/route-loading.md, "Pitfalls").
 */

/** A text line's box at its loaded line height, with a bar centred in it. */
function Line({ line, bar }: { line: string; bar: string }) {
  return (
    <div className={`flex items-center ${line}`}>
      <Skeleton className={`rounded-xs ${bar}`} />
    </div>
  )
}

/** Six cards, the typical first screen: each the loaded `BasketTile`'s hairline box, 236 inside. */
const LIST_CARDS = 6

function CardsSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-7 items-center justify-between">
        <Skeleton className="h-5 w-32 rounded-xs" />
        <Skeleton className="h-3 w-16 rounded-xs" />
      </div>
      <div className={BASKET_GRID}>
        {Array.from({ length: LIST_CARDS }, (_, index) => (
          <div key={index} className="border border-border-subtle">
            <Skeleton className="h-59 w-full" />
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * `/baskets` before its list: the search field and the categories menu on one row, then the
 * first section's head and its cards. `cardsOnly` is the section alone,
 * for a list whose first read failed and is being tried again under controls already drawn.
 */
export function BasketsListSkeleton({ cardsOnly = false }: { cardsOnly?: boolean }) {
  if (cardsOnly) return <CardsSkeleton />
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Skeleton className={`h-11 lg:h-9 ${BASKET_SEARCH}`} />
        <Skeleton className="h-11 w-32 max-md:ml-auto lg:h-9" />
      </div>
      <CardsSkeleton />
    </div>
  )
}

/**
 * The loaded `TitleRow`: the back button and the round cover centred on the title block, which is
 * the 16px kicker line, the 36px title line and the 20px tagline.
 */
export function BasketHeaderSkeleton() {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-9 shrink-0 items-center">
        <BackButton to="/baskets" />
      </div>
      <Skeleton className="mr-2 size-11 shrink-0 rounded-full sm:size-13" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Line line="h-4" bar="h-3 w-32" />
        <Line line="h-9" bar="h-6 w-72 max-w-full rounded-sm" />
        <Line line="h-5" bar="h-3 w-96 max-w-full" />
      </div>
    </div>
  )
}

/**
 * A basket's page before its data: the two-figure strip, the tab strip (44px, 36 from `lg`), and
 * the Members table at a typical ten two-line rows of four columns.
 */
export function BasketDetailSkeleton() {
  return (
    <>
      <StatStripSkeleton cells={2} />
      <div className="flex flex-col gap-4">
        <div className="flex h-11 items-center gap-5 lg:h-9">
          <Skeleton className="h-4 w-20 rounded-xs" />
          <Skeleton className="h-4 w-28 rounded-xs" />
          <Skeleton className="h-4 w-20 rounded-xs" />
        </div>
        <DataTableSkeleton rows={10} columns={4} rowHeight="lg" />
      </div>
    </>
  )
}
